import {routing} from '@cl/shared';
import {ClinchEvent} from 'common/src/hooks/useClinchEvent';
import * as authService from 'common/src/modules/auth/auth.service';
import _isEmpty from 'lodash/isEmpty';
import {MouseEvent as ReactMouseEvent} from 'react';
import {allRouteDefinitions} from './routing.config';
import {accountURLParam} from './routing.consts';
import {RouteKey} from './routing.enums';
import {
  AppRoute,
  NavigationEntry,
  RouteChangeEventData,
  RouteConfig,
  RouteDef,
} from './routing.types';
import getCurrentLocationUrl from './utils/getCurrentLocationUrl';
import getCurrentRouteWithParams from './utils/getCurrentRouteWithParams';
import getRouteKey from './utils/getRouteKey';
import isMatchRoute from './utils/isMatchRoute';
import recordNavigation from './utils/recordNavigation';
import registerDomainSpecificRouteFunctions from './utils/registerDomainSpecificRouteFunctions';
import waitForPopstate from './utils/waitForPopstate';

const {beforeGoListeners} = routing;

let navigationHistory: NavigationEntry[] = [];

function initializeHistoryTracking() {
  navigationHistory = recordNavigation(
    navigationHistory,
    window.location.pathname + window.location.search,
  );

  document.addEventListener(ClinchEvent.CLEInterAppRedirect, ((
    event: CustomEvent<RouteChangeEventData>,
  ) => {
    navigationHistory = recordNavigation(navigationHistory, event.detail.url);
  }) as EventListener);

  window.addEventListener('popstate', () => {
    navigationHistory = recordNavigation(
      navigationHistory,
      window.location.pathname + window.location.search,
    );
  });
}

function getNavigationHistory(): NavigationEntry[] {
  return [...navigationHistory];
}

function goTo(route: AppRoute) {
  const url = createUrl(route);

  if (!executeBeforeGoCallbacks()) return;

  if (toConfig(route).reload) {
    document.location.href = url;
  } else {
    historyChangeCallback(url);
  }
}

function redirectTo(route: AppRoute) {
  const url = createUrl(route);
  if (!executeBeforeGoCallbacks()) return;
  historyChangeCallback(url, true);
}

async function goBack(): Promise<void> {
  window.history.back();
  await waitForPopstate();
}

async function goForward(): Promise<void> {
  window.history.forward();
  await waitForPopstate();
}

function executeBeforeGoCallbacks() {
  return beforeGoListeners.current.every(({callback}: {callback: () => boolean}) => callback());
}

function historyChangeCallback(targetUrl: string, replaceState = false) {
  document.dispatchEvent(
    new CustomEvent<RouteChangeEventData>(ClinchEvent.CLEInterAppRedirect, {
      detail: {url: targetUrl, replaceState},
    }),
  );
}

function createUrl(route: AppRoute, relative = true): string {
  if (typeof route === 'string') {
    route = {url: route};
  }

  const pathParams = {...route?.pathParams};

  const {clientId} = authService.getUserAuth();
  pathParams[accountURLParam] = route.accountId || String(clientId);

  let [url] = route.url.split('?');

  const regExp = new RegExp(
    Object.keys(pathParams || {})
      .map((p) => `(:${p})`)
      .join('|'),
    'g',
  );
  url = url.replace(regExp, (x: string) => pathParams[x.substring(1)]?.toString());

  if (!_isEmpty(route?.queryParams)) {
    url += `?${new URLSearchParams(route?.queryParams as Record<string, string>).toString()}`;
  }

  if (!relative) {
    url = `${location.origin}${url}`;
  }

  return url;
}

function toConfig(route: AppRoute): RouteConfig {
  return typeof route === 'string' ? {url: route} : route;
}

function openInWindow(route: AppRoute, newWindow = false): Window | null {
  if (newWindow) {
    const w = window.open(createUrl(route), '_blank');
    w?.focus();

    return w;
  } else {
    return window.open(createUrl(route));
  }
}

function openLinkCallback(route: AppRoute) {
  return (e: MouseEvent | ReactMouseEvent) => {
    e.ctrlKey || e.metaKey ? openInWindow(route) : goTo(route);
  };
}

function registerBeforeGoListener(callback: () => boolean) {
  const subscriptionId = Math.random().toString(36);
  beforeGoListeners.current.push({id: subscriptionId, callback});

  return subscriptionId;
}

function unregisterBeforeGoListener(subscription: string) {
  beforeGoListeners.current = beforeGoListeners.current.filter(
    ({id}: {id: string}) => id !== subscription,
  );
}

function getPathParamFromUrl(baseRoute: string, param: string) {
  const accountReg = /\/acc\/.*?\//g;
  const baseUntilParam = baseRoute.replace(accountReg, '/').split(param)[0];
  const numOfSlashes = baseUntilParam.match(/\//g)?.length ?? 0;
  const reg = new RegExp(`(\/.*?){${numOfSlashes}}`, 'g');

  return document.location.pathname.replace(accountReg, '/').replace(reg, '')?.replace(/\/.*/, '');
}

function getRouteDefinition(routeKey: RouteKey): RouteDef {
  return allRouteDefinitions[routeKey];
}

function getRoutePath(routeKey: RouteKey): string {
  return allRouteDefinitions[routeKey].path;
}

const routingService = {
  goTo,
  redirectTo,
  goBack,
  goForward,
  createUrl,
  openInWindow,
  openLinkCallback,
  registerBeforeGoListener,
  unregisterBeforeGoListener,
  isMatchRoute,
  getPathParamFromUrl,
  getRouteKey,
  getCurrentRouteWithParams,
  registerDomainSpecificRouteFunctions,
  getRouteDefinition,
  getRoutePath,
  initializeHistoryTracking,
  getNavigationHistory,
  getCurrentLocationUrl,
};

export default routingService;
